import { useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { vendorBySlug, LOGOS, getBrochureUrl } from "@/lib/vendors";
import BgLayer from "@/components/ui-shared/BgLayer";
import BrochureModal from "@/components/ui-shared/BrochureModal";

const deriveLevel = (title) => {
    const t = title.toLowerCase();
    if (/(foundation|fundamental|basic|essential|associate|introduction|starter|beginner)/.test(t)) return "Foundation";
    if (/(advanced|expert|specialist|master|professional|architect|lead|principal)/.test(t)) return "Advanced";
    return "Intermediate";
};

const deriveType = (title) => {
    const t = title.toLowerCase();
    if (/(program|path|journey|track|specialization|bootcamp|certificate)/.test(t)) return "Learning Path";
    return "Course";
};

const CourseCard = ({ course, vendor, logo, onDownload, hasBrochure }) => {
    const navigate = useNavigate();
    const level = deriveLevel(course.title);
    const type = deriveType(course.title);
    const cardImage = vendor.courseBanner || vendor.banner;
    const hasDetailPage = !!course.details;
    const clickable = hasDetailPage || hasBrochure;
    const handleClick = hasDetailPage
        ? () => navigate(`/partners/${vendor.slug}/courses/${course.id}`)
        : clickable
            ? () => onDownload(course.title, course.id)
            : undefined;
    const Wrapper = clickable ? motion.button : motion.div;
    return (
        <Wrapper
            type={clickable ? "button" : undefined}
            onClick={handleClick}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45 }}
            className={`group text-left bg-white rounded-2xl shadow-[0_8px_28px_-12px_rgba(9,3,20,0.6)] transition-all duration-400 flex flex-col overflow-hidden ${
                clickable
                    ? "hover:shadow-[0_18px_44px_-14px_rgba(161,0,255,0.35)] hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-[#A100FF]/40"
                    : "opacity-70"
            }`}
            data-testid={`course-card-${course.id}`}
        >
            {/* Image */}
            <div className="relative h-44 w-full">
                <div
                    className="absolute inset-0 overflow-hidden rounded-t-2xl"
                    style={{ background: `linear-gradient(135deg, ${vendor.hue}55 0%, #07020F 100%)` }}
                >
                    <img
                        src={cardImage}
                        alt={course.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                </div>
                {/* Brand logo pill — overlapping bottom-left */}
                <div className="absolute -bottom-6 left-5 z-10 bg-white rounded-xl shadow-[0_10px_24px_-8px_rgba(9,3,20,0.55)] px-4 py-2.5 flex items-center gap-2.5 h-12 min-w-[120px]">
                    {logo && (
                        <img
                            src={logo}
                            alt=""
                            className="h-5 w-5 object-contain shrink-0"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                    )}
                    <span className="font-display font-semibold text-[15px] tracking-tight text-[#0E1220] whitespace-nowrap">
                        {vendor.name.split(/[\s(–-]/)[0]}
                    </span>
                </div>
            </div>

            {/* Body */}
            <div className="pt-10 pb-6 px-5 flex flex-col flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span
                        className="inline-flex items-center rounded-full px-3 py-1 text-[12px] font-medium tracking-tight"
                        style={{ background: "#EEF1F6", color: "#4B5568" }}
                        data-testid={`course-chip-type-${course.id}`}
                    >
                        {type}
                    </span>
                    <span
                        className="inline-flex items-center rounded-full px-3 py-1 text-[12px] font-medium tracking-tight"
                        style={{ background: "#EEF1F6", color: "#4B5568" }}
                        data-testid={`course-chip-level-${course.id}`}
                    >
                        {level}
                    </span>
                </div>

                <h3 className="mt-4 font-display text-[#0E1220] text-[18px] leading-snug font-semibold tracking-tight">
                    {course.title}
                </h3>

                {/* Subtle hover reveal — matches reference minimalism */}
                {clickable && (
                    <div className="mt-4 flex items-center justify-between border-t border-[#EEF1F6] pt-4">
                        <span className="text-[12px] uppercase tracking-widest font-display text-[#8A93A6]">
                            {hasDetailPage ? "Course details" : "Brochure"}
                        </span>
                        <span
                            className="text-[13px] font-display font-semibold text-[#7B00CC] group-hover:text-[#A100FF] transition-colors"
                            data-testid={`course-brochure-cta-${course.id}`}
                        >
                            {hasDetailPage ? "View course →" : "Download →"}
                        </span>
                    </div>
                )}
            </div>
        </Wrapper>
    );
};

const PartnerDetailPage = () => {
    const { slug } = useParams();
    const vendor = vendorBySlug(slug);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeCourse, setActiveCourse] = useState(null);
    const [activeCourseId, setActiveCourseId] = useState(null);
    const [activeCategory, setActiveCategory] = useState("All");
    if (!vendor) return <Navigate to="/partners" replace />;
    const openModal = (title, id) => { setActiveCourse(title); setActiveCourseId(id); setModalOpen(true); };
    const logo = LOGOS[vendor.slug];

    const categories = [...new Set(vendor.courses.map((c) => c.category).filter(Boolean))];
    const visibleCourses = activeCategory === "All"
        ? vendor.courses
        : vendor.courses.filter((c) => c.category === activeCategory);

    const sortedCourses = [...visibleCourses].sort((a, b) => {
        const aHas = vendor.brochureFiles ? !!getBrochureUrl(vendor, a.title) : true;
        const bHas = vendor.brochureFiles ? !!getBrochureUrl(vendor, b.title) : true;
        return aHas === bHas ? 0 : aHas ? -1 : 1;
    });

    return (
        <div data-testid={`partner-page-${slug}`}>
            {/* Hero banner */}
            <section className="relative pt-32 md:pt-40 pb-14 overflow-hidden">
                <BgLayer variant="mesh" intensity="strong" />
                <div
                    className="absolute inset-0 opacity-30"
                    style={{ backgroundImage: `linear-gradient(135deg, ${vendor.hue}22 0%, transparent 60%)` }}
                />
                <div className="sxe-container relative grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
                    <div>
                        <Link to="/partners" className="inline-flex items-center gap-1.5 text-[#B0A3C4] hover:text-white text-sm font-display">
                            <ArrowLeft className="w-3.5 h-3.5" /> All Partners
                        </Link>
                        <div className="mt-4 overline"><span className="dot" /> Partner Offering</div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.02] text-white mt-5">
                            <span className="text-gradient">{vendor.name}</span>
                        </h1>
                        <p className="text-[#B0A3C4] text-lg md:text-xl leading-relaxed mt-6 max-w-xl">{vendor.tagline}</p>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative rounded-2xl overflow-hidden border border-[#C266FF]/25"
                    >
                        <div
                            className="w-full h-[360px] flex items-center justify-center"
                            style={{ background: `linear-gradient(135deg, ${vendor.hue}55 0%, #07020F 100%)` }}
                        >
                            {vendor.seal ? (
                                <img src={vendor.seal} alt={`${vendor.name} Authorized Training Partner`} className="w-56 h-56 object-contain" />
                            ) : (
                                <img
                                    src={vendor.banner}
                                    alt={vendor.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                            )}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] via-[#07020F]/30 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
                            <div
                                className="w-14 h-14 rounded-xl bg-white flex items-center justify-center overflow-hidden shadow-[0_8px_20px_-8px_rgba(0,0,0,0.6)]"
                                style={{ borderTop: `2px solid ${vendor.hue}` }}
                            >
                                {logo ? (
                                    <img src={logo} alt={`${vendor.name} logo`} className="max-w-[70%] max-h-[70%] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                ) : (
                                    <span className="font-display font-bold text-[13px] tracking-tight" style={{ color: vendor.hue }}>
                                        {vendor.name.replace(/[^A-Z]/g, "").slice(0, 3) || vendor.name.slice(0, 3).toUpperCase()}
                                    </span>
                                )}
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-widest text-[#E9CCFF] font-display">Certified programs</div>
                                <div className="font-display text-white text-lg font-medium">{vendor.courses.length}+ courses</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* About */}
            <section className="sxe-section relative bg-[#08031a]">
                <BgLayer variant="dots" />
                <div className="sxe-container relative">
                    <div className="max-w-3xl">
                        <div className="overline"><span className="dot" /> About the partner</div>
                        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                            SpringXE × <span className="text-gradient">{vendor.name}</span>
                        </h2>
                        <p className="text-[#B0A3C4] text-lg leading-relaxed mt-6">{vendor.about}</p>
                    </div>
                </div>
            </section>

            {/* Courses */}
            <section id="courses" className="sxe-section relative">
                <BgLayer variant="orbs" />
                <div className="sxe-container relative">
                    <div className="max-w-3xl">
                        <div className="overline"><span className="dot" /> Courses & Certifications</div>
                        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                            Programs mapped to <span className="text-gradient">{vendor.name}</span>
                        </h2>
                    </div>

                    {categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-8" data-testid="course-category-filters">
                            {["All", ...categories].map((cat) => (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => setActiveCategory(cat)}
                                    data-testid={`category-filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                                    className={`px-4 py-2 rounded-full text-sm font-display font-medium tracking-tight border transition-colors ${
                                        activeCategory === cat
                                            ? "bg-[#A100FF] border-[#A100FF] text-white"
                                            : "border-white/15 text-[#B0A3C4] hover:border-[#C266FF]/50 hover:text-white"
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
                        {sortedCourses.map((c) => (
                            <CourseCard
                                key={c.id}
                                course={c}
                                vendor={vendor}
                                logo={logo}
                                onDownload={openModal}
                                hasBrochure={vendor.brochureFiles ? !!getBrochureUrl(vendor, c.title) : true}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <BrochureModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                courseTitle={activeCourse}
                courseId={activeCourseId}
                vendorSlug={vendor.slug}
                vendorName={vendor.name}
                brochureUrl={getBrochureUrl(vendor, activeCourse)}
            />
        </div>
    );
};

export default PartnerDetailPage;
